import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useConfig from "@/hooks/useConfig";
import { useChannels } from "../Events/api";
import {
  createProgram,
  fetchGameDuration,
  fetchSchedule,
  patchProgram,
  removeProgram,
  LIVE_CHANNEL_IDS,
  type ProgramPatch,
} from "./api";
import { apiToProgram, programToApiBlock } from "./apiMapping";
import { getWeekDateKeys, shiftDateKey, stripDateKeys } from "./schedulerUtils";
import type { Channel, Program, SchedulerView } from "./types";

export interface RemoteSchedule {
  /** The 24x7 channels (central API rows behind the allowlist), [] while loading. */
  channels: Channel[];
  /** Date keys the current fetch covers (inclusive). */
  rangeDays: string[];
  /** Programs the server holds for that range, undefined until loaded. */
  programs: Program[] | undefined;
  /** POST one program onto its channel-day; resolves to the server row. */
  addProgram: (program: Program) => Promise<Program>;
  /** PATCH one program's changed fields, addressed at its source channel-day. */
  patchProgram: (
    sourceChannelId: string,
    sourceDate: string,
    programId: string,
    fields: ProgramPatch,
  ) => Promise<Program>;
  /** DELETE one program from its channel-day. */
  removeProgram: (channelId: string, date: string, programId: string) => Promise<void>;
  /** A game's exact runtime in seconds, or null when the probe cannot answer. */
  getGameDuration: (gameId: string) => Promise<number | null>;
  /** Refetch the loaded range, snapping local state back to server truth. */
  refetchDays: () => void;
}

/**
 * Everything remote the scheduler needs, in one seam: channels from the
 * central API (allowlisted to the 24x7 three), the stored schedule for the
 * visible range, and the per-day save. The rest of useChannelScheduler is
 * pure local state on top of this, and tests mock this module.
 */
export function useRemoteSchedule(view: SchedulerView, date: string): RemoteSchedule {
  const queryClient = useQueryClient();
  const config = useConfig();
  const customer = String(config?.customer ?? "");
  const channelsQuery = useChannels(customer);

  const channels = useMemo<Channel[]>(() => {
    const rows = channelsQuery.data ?? [];
    return rows
      .filter((row) => LIVE_CHANNEL_IDS.includes(Number(row.channel_id)))
      .map((row) => ({
        id: String(row.channel_id),
        name: row.channel_name,
        logo: row.channel_name,
        logoUrl: row.channel_logo_url ?? "",
      }));
  }, [channelsQuery.data]);

  // Day view covers the whole scrollable strip; both views also fetch one
  // day before their first, because a block on that day can spill past
  // midnight into the visible range (blocks live on the day they start).
  const rangeDays = useMemo(() => {
    const days = view === "week" ? getWeekDateKeys(date) : stripDateKeys(date);
    return [shiftDateKey(days[0], -1), ...days];
  }, [view, date]);
  const from = rangeDays[0];
  const to = rangeDays[rangeDays.length - 1];

  const scheduleQuery = useQuery({
    queryKey: ["v2", "channel-schedule", "admin", from, to],
    queryFn: async () => (await fetchSchedule(from, to)).map(apiToProgram),
  });

  const ops = useMemo(
    () => ({
      addProgram: async (program: Program) => {
        const row = await createProgram(
          Number(program.channelId),
          program.date,
          programToApiBlock(program),
        );
        return apiToProgram(row);
      },
      patchProgram: async (
        sourceChannelId: string,
        sourceDate: string,
        programId: string,
        fields: ProgramPatch,
      ) => {
        const row = await patchProgram(
          Number(sourceChannelId),
          sourceDate,
          Number(programId),
          fields,
        );
        return apiToProgram(row);
      },
      removeProgram: (channelId: string, date: string, programId: string) =>
        removeProgram(Number(channelId), date, Number(programId)),
      getGameDuration: (gameId: string) => fetchGameDuration(gameId),
    }),
    [],
  );

  const refetchDays = useMemo(
    () => () =>
      void queryClient.invalidateQueries({ queryKey: ["v2", "channel-schedule", "admin"] }),
    [queryClient],
  );

  return { channels, rangeDays, programs: scheduleQuery.data, ...ops, refetchDays };
}
